import Box from '@components/Box/Box';
import boxIcon from '@assets/icons/box_blue_19.svg';
import DisposalProcessingModalWindow from '../modals/DisposalProcessingModalWindow';
import DocumentLink from '@components/DocumentLink';
import FpbInstruction from '../modals/FpbInstruction';
import HardConfirmPhoneModalWindow from '@components/modals/HardConfirmPhoneModalWindow';
import infoIcon from '@assets/icons/info_blue_19.svg';
import NoItems from '@components/NoItems';
import ParcelModalWindow from '@containers/ParcelModalWindow';
import PaymentCompletedModalWindow from '@components/modals/PaymentCompletedModalWindow';
import PaymentModalWindow from '@components/modals/PaymentModalWindow';
import PlacingOrderModalWindow from '@components/modals/PlacingOrderModalWindow';
import ResaleModalWindow from '../modals/ResaleModalWindow';
import UnpaidTable from '../components/UnpaidTable';
import WarningIcon from '@assets/icons/warning.svg';
import { actionAddresses } from '@utils/handlerSubmitActions';
import { AddSellerPanel } from '@components/AddSeller';
import { Button, ButtonType } from '@components/Button';
import {
  checkBalanceReplenishment,
  checkBalanceReplenishmentResponse,
  getPaymentLink,
  getWallet,
  sendTopUpBalance,
  sendTopUpBalanceResponse,
  TGetPaymentLinkResponse
} from '@api/apiClient/finance';
import {
  checkReturnOrderStatus,
  Return,
  TCheckOrderStatusResponse,
  TReturnOrderAddress,
  TReturnOrderResponse,
  useUnpaidCounter
} from '@api/apiClient';
import { createReturnOrder, TReturnOrderParams } from '@api/apiClient';
import {
  EPlacingOrder,
  TPlacingOrderData,
  TPlacingOrderDataItem
} from '@components/modals/PlacingOrderModalWindow/types';
import { EPlacingOrderViewParcel, getPlacingOrderView } from '@utils/createPlacingOrderView';
import { FilterModel } from '@hooks/useFiltersSearchParams';
import { getCurrentLanguage, Langs } from '@utils/lang';
import { Grid, Row } from '@components/Grid';
import { Modal } from '@components/Modal';
import { Text, TextSize, TextType, TextWeight } from '@components/Text';
import { TPlacingOrderListItem } from '@components/PlacingOrderView/types';
import { useMutation } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useProfile } from '@contexts/ProfileContext';
import { useState, useCallback, ReactElement, useRef } from 'react';
import { useTranslation } from 'react-i18next';

const urlPrefix = "https://unitrade.fra1.digitaloceanspaces.com/public/";

interface LinkData {
  fileSize: string;
  url: string;
}

const prohibited: Record<Langs, LinkData> = {
  [Langs.en]: {
    fileSize: "28,6 KB",
    url: `${urlPrefix}sellers/List-of-goods-prohibited-for-return-to-China.xlsx`,
  },
  [Langs.cn]: {
    fileSize: "28,4 KB",
    url: `${urlPrefix}sellers/list-ch.xlsx`,
  },
  [Langs.ru]: {
    fileSize: "40,9 KB",
    url: `${urlPrefix}sellers/list-restrict.docx`,
  },
  [Langs.tr]: {
    fileSize: "63,5 KB",
    url: "https://storage.yandexcloud.net/unitrade-public/public/zapret.odt",
  },
};

const instruction: Record<Langs, LinkData | null> = {
  [Langs.tr]: {
    fileSize: "2,69 MB",
    url: `${urlPrefix}pdf/seller-instruction_TR.pdf`,
  },
  [Langs.cn]: {
    fileSize: "2,5 MB",
    url: `${urlPrefix}pdf/seller-instruction_CN2.pdf`,
  },
  [Langs.en]: null,
  [Langs.ru]: null,
};

const UnpaidTab = ({
  filters,
  pending,
  downloadingBtn,
  handlerSetSavedSelection,
  savedSelection,
  refreshData,
  sellersNotFound,
  noData,
}: {
  filters: FilterModel[],
  pending: boolean,
  downloadingBtn: ReactElement,
  handlerSetSavedSelection: (ids: string[], values: Return[]) => void,
  savedSelection: string[],
  savedReturnsSelection?: Return[],
  refreshData: () => void,
  sellersNotFound: boolean,
  noData: boolean,
}) => {
  const [detailId, setDetailId] = useState("");
  const [isFpbInstructionOpen, setFpbInstructionOpen] = useState(false);
  const [isHardConfirmOpen, setIsHardConfirmOpen] = useState(false);
  const [isOpenParcelWindow, setIsOpenParcelWindow] = useState(false);
  const [isOpenPaymentCompletedWindow, setIsOpenPaymentCompletedWindow] = useState<boolean>(false);
  const [isOpenPaymentWindow, setIsOpenPaymentWindow] = useState<boolean>(false);
  const [isRecyclingOpen, setIsRecyclingOpen] = useState(false);
  const [isResalesOpen, setIsResalesOpen] = useState(false);
  const [isSentReturnsOpen, setIsSentReturnsOpen] = useState(false);
  const [orderInProcessed, setOrderInProcessed] = useState<boolean>(false);
  const [parcelsGoodView, setParcelsGoodView] = useState<TPlacingOrderListItem[] | undefined>(undefined);
  const [paymentError, setPaymentError] = useState<string | undefined>(undefined);
  const [paymentInProcessed, setPaymentInProcessed] = useState<boolean>(false);
  const [resale, setResale] = useState<Return | null>(null);
  const [selectedDelivery, setSelectedDelivery] = useState<number>(0);
  const [selectedDepartureCount, setSelectedDepartureCount] = useState<number>(0);
  const [selectedParcels, setSelectedParcels] = useState<Map<string, Return>>(new Map());
  const [selectedSum, setSelectedSum] = useState<number>(0);
  const [submitParams, setSubmitParams] = useState<TReturnOrderParams | undefined>(undefined);

  const timeoutCheckBalance = useRef<number | null>(null);
  const timeoutCheckPayment = useRef<number | null>(null);
  const timeoutCheckStatus = useRef<number | null>(null);

  const counter = useUnpaidCounter(!sellersNotFound);
  const currentLanguage = getCurrentLanguage();
  const navigate = useNavigate();
  const { account, get } = useProfile();
  const { t } = useTranslation();

  const handleSelectionChange = useCallback((
    selectedIds: string[],
    items: Return[],
  ) => {
    const parcels: Map<string, Return> = new Map();
    selectedParcels.forEach((parcel, key) => {
      const item = items.find((v) => v.id === key);
      if (!item) parcels.set(key, parcel) // selected items from other pages
    });

    selectedIds.forEach((id) => {
      const item = items.find((v) => (v.id === id));
      if (item) {
        parcels.set(id, item);
      } else {
        // selected items from other pages
        const selectedItem = selectedParcels.get(id);
        if (!selectedItem) {
          console.error(`product with id ${id} not found`);
        } else {
          parcels.set(id, selectedItem);
        }
      }
    })
    setSelectedParcels(parcels);
    const selectedArray = Array.from(parcels, ([_, value]) => value);
    handlerSetSavedSelection(selectedIds, selectedArray);

    const parcelsArray: Return[] = Array.from(parcels.values());

    const parcelsGoodView: TPlacingOrderListItem[] =
        getPlacingOrderView(parcelsArray, EPlacingOrderViewParcel.UNCLAIMED_UNPAID_RETURN);

    const sum = +selectedArray.reduce((acc, item) => (acc + (item.declaredValue?.amount ?? 0)), 0).toFixed(2);
    const delivery = +selectedArray.reduce((acc, item) => (acc + (item.deliveryPrice?.amount ?? 0)), 0).toFixed(2);

    setParcelsGoodView(parcelsGoodView);
    setSelectedSum(sum);
    setSelectedDelivery(delivery);
    setSelectedDepartureCount(selectedArray.filter((item: Return) => item.isDeparture).length);
  }, [selectedParcels]);

  const closeParcelWindow = () => {
    setIsOpenParcelWindow(false);
  }

  const handleParcelClick = (parcelId: string) => {
    setIsOpenParcelWindow(true)
    setDetailId(parcelId)
  }

  const closeFpbInstruction = () => setFpbInstructionOpen(false);

  const { mutate: mutateCheckBalanceReplenishment } = useMutation({
    mutationFn: checkBalanceReplenishment
  });

  const { mutate: mutateCheckBalance } = useMutation({
    mutationFn: getWallet,
    onSuccess: (result) => {
      if (result.balance >= selectedDelivery) {
        mutateSendCreateReturnOrder(submitParams!);
      } else {
        waitingBalanceReplenished();
      }
    }
  });

  const { mutate: mutateCheckReturnOrderStatus } = useMutation({
    mutationFn: checkReturnOrderStatus
  });

  const {
    data: dataSendTopUpBalance,
    error: errorSendTopUpBalance,
    isError: isErrorSendTopUpBalance,
    isLoading: isLoadingSendTopUpBalance,
    mutate: mutateSendTopUpBalance,
    reset: resetSendTopUpBalance
  } = useMutation({
    mutationFn: sendTopUpBalance,
    onSuccess: (result: sendTopUpBalanceResponse) => {
      setIsOpenPaymentWindow(true);
      setIsSentReturnsOpen(false);
      setPaymentTimeout();
      waitingBalanceReplenished(result.outTradeNo);
    }
  });

  const {
    error: errorGetPaymentLink,
    isError: isErrorGetPaymentLink,
    isLoading: isLoadingGetPaymentLink,
    mutate: mutateGetPaymentLink
  } = useMutation({
    mutationFn: getPaymentLink,
    onSuccess: (result: TGetPaymentLinkResponse) => {
      setPaymentInProcessed(true);
      setPaymentTimeout();
      waitingBalanceReplenished();
      window.open(result.paymentLink);
    }
  });

  const {
    error: errorSendCreateReturnOrder,
    isError: isErrorSendCreateReturnOrder,
    isLoading: isLoadingSendCreateReturnOrder,
    mutate: mutateSendCreateReturnOrder,
    reset: resetSendCreateReturnOrder
  } = useMutation({
    mutationFn: createReturnOrder,
    onSuccess: (result: TReturnOrderResponse) => {
      setOrderInProcessed(true);
      waitingOrderStatus(result.orderId);
    }
  });

  const waitingBalanceReplenished = (id?: string): void => {
    timeoutCheckBalance.current = window.setTimeout(() => {
      if (id) {
        mutateCheckBalanceReplenishment(id, {
          onSuccess: (data: checkBalanceReplenishmentResponse) => {
            if (data.isProcess) {
              if (!paymentInProcessed) setPaymentInProcessed(true);
              waitingBalanceReplenished(id);
            } else {
              if (paymentInProcessed) setPaymentInProcessed(false);
              if (data.isSuccess) {
                clearTimeout(timeoutCheckPayment.current as number);
                mutateCheckBalance();
              } else {
                if (data.errorCode) {
                  clearTimeout(timeoutCheckPayment.current as number);
                  setPaymentError(data.errorCode);
                } else {
                  waitingBalanceReplenished(id);
                }
              }
            }
          }
        });
      } else {
        mutateCheckBalance();
      }
    }, 3000);
  };

  const waitingOrderStatus = (returnOrderId: string): void => {
    timeoutCheckStatus.current = window.setTimeout(() => {
      mutateCheckReturnOrderStatus(returnOrderId, {
        onSuccess: (data: TCheckOrderStatusResponse) => {
          if (data.status === 'Approved') {
            counter.refetch();
            get();
            handlerSetSavedSelection([], []);
            onCloseReturnProcessing();
            refreshData();
            setIsOpenPaymentCompletedWindow(true);
            setIsOpenPaymentWindow(false);
            setOrderInProcessed(false);
            setPaymentInProcessed(false);
            setSelectedParcels(new Map());
          } else {
            waitingOrderStatus(returnOrderId);
          }
        }
      });
    }, 3000);
  };

  const setPaymentTimeout = (): void => {
    timeoutCheckPayment.current = window.setTimeout(() => {
      clearTimeout(timeoutCheckBalance.current as number);
      setPaymentInProcessed(false);
    }, 600000);
  };

  const onClosePaymentReturn = (): void => {
    clearTimeout(timeoutCheckBalance.current as number);
    clearTimeout(timeoutCheckPayment.current as number);
    setIsOpenPaymentWindow(false);
    setPaymentInProcessed(false);
  };

  const onClickPaymentLink = (amount: string) => {
    mutateGetPaymentLink(amount);
  };

  const onCloseReturnProcessing = () => {
    resetSendCreateReturnOrder();
    resetSendTopUpBalance();
    setIsSentReturnsOpen(false);
    setPaymentInProcessed(false);
  };

  const handlerSubmit = (forms: TPlacingOrderData): void => {
    const addresses: TReturnOrderAddress[] = forms.map((item: TPlacingOrderDataItem) => ({
      address: item.address,
      contactInfo: {
        name: item.fullName,
        phone: item.phone
      }
    }));
    const params: TReturnOrderParams = {
      addresses: addresses,
      parcelIds: parcelsGoodView!.map((item: TPlacingOrderListItem) => item.id)
    };
    if (account!.balance >= selectedDelivery) {
      mutateSendCreateReturnOrder(params);
    } else {
      setSubmitParams(params);
      if (account!.region === 'TR') {
        onCloseReturnProcessing();
        setIsOpenPaymentWindow(true);
      } else {
        mutateSendTopUpBalance({
          amount: Math.round((selectedDelivery - account!.balance) * 100) / 100
        });
      }
    }
    actionAddresses(forms);
  };

  return (
    <div>
      <Grid>
        <Row>
          <Box textAlign="left" pb={currentLanguage !== Langs.cn ? 7 : 0}>
            <Row gap={6}>
              <DocumentLink
                icon={infoIcon}
                text={t('awaitingPage.downloadInstruction')}
                {...(instruction[currentLanguage] || instruction[Langs.cn]) as LinkData}
              />
              <DocumentLink
                icon={boxIcon}
                text={t('awaitingPage.itemsProhibitedReturn')}
                {...(prohibited[currentLanguage])}
              />
              {(account?.region &&
                (account?.region !== "TR")) ? (
                <Button
                  type={ButtonType.LINK}
                  onClick={() => setFpbInstructionOpen(true)}
                  icon={WarningIcon}
                  title={(
                    <Text weight={TextWeight.MEDIUM} size={TextSize.L}>
                      {t("fbpInstruction.link")}
                    </Text>
                  )}
                />
              ) : null}
            </Row>
          </Box>
        </Row>
      </Grid>

      {sellersNotFound ? (
        <AddSellerPanel
          isFirstSeller
          onSuccess={() => {
            refreshData();
            handlerSetSavedSelection([], []);
            setSelectedParcels(new Map());
            counter.refetch();
          }}
        />
      ) : (
        noData ? (
          <NoItems
            title={t("noItems.returns.title")}
            description={t("noItems.returns.description")}
          />
        ) : (
          <>
            <div style={{ marginBottom: "20px" }}>
              <Text size={TextSize.M} type={TextType.BLOCK}>
                {t("awaitingPage.unpaid_description1")}
              </Text>
              <Text size={TextSize.M} type={TextType.BLOCK}>
                {t("awaitingPage.unpaid_description2")}
              </Text>
              <Text size={TextSize.M} type={TextType.BLOCK}>
                {t("awaitingPage.unpaid_description3")}
              </Text>
              <Text size={TextSize.M} type={TextType.BLOCK}>
                {t("awaitingPage.unpaid_description4")}
              </Text>
              <Text size={TextSize.M} type={TextType.BLOCK}>
                {t("awaitingPage.unpaid_description5")}
              </Text>
            </div>
            <UnpaidTable
              key="unpaidTable"
              onParcelClick={handleParcelClick}
              onClickDeliver={() => {
                setIsSentReturnsOpen(true);
              }}
              onClickDisposal={() => {
                setIsRecyclingOpen(true);
              }}
              onClickResale={(resale) => {
                setIsResalesOpen(true);
                setResale(resale);
              }}
              onClickPhoneConfirm={() => {
                setIsHardConfirmOpen(true);
              }}
              onSelectionChange={handleSelectionChange}
              selectedRowIds={savedSelection}
              selectedSum={selectedSum}
              pending={pending}
              filters={filters}
              downloadingBtn={downloadingBtn}
              selectedDelivery={selectedDelivery}
              selectedDepartureCount={selectedDepartureCount}
            />
          </>
      ))}

      {/* Утилизация */}
      <Modal
        isOpen={isRecyclingOpen}
        onClose={() => {
          setIsRecyclingOpen(false);
        }}
        onClickOverlay={() => {
          setIsRecyclingOpen(false);
        }}
        width={650}
      >
        <DisposalProcessingModalWindow
            handlerOnCloseDisposalProcessing={() => {
              setIsRecyclingOpen(false);
            }}
            handlerRefreshData={() => {
              handlerSetSavedSelection([], []);
              refreshData();
              setSelectedParcels(new Map());
              setIsRecyclingOpen(false);
              counter.refetch();
            }}
            selectedRowIds={savedSelection}
        />
      </Modal>

      {/* Возвраты */}
      <Modal
          isOpen={isSentReturnsOpen}
          onClickOverlay={() => setIsSentReturnsOpen(false)}
          onClose={() => setIsSentReturnsOpen(false)}
          width={650}
      >
        <PlacingOrderModalWindow
            costOfService={selectedDelivery}
            errorSendPlacingOrder={errorSendCreateReturnOrder}
            errorSendTopUpBalance={errorSendTopUpBalance}
            handlerOnClosePlacingOrderProcessing={onCloseReturnProcessing}
            handlerResetSendOrder={resetSendCreateReturnOrder}
            handlerSubmit={handlerSubmit}
            isErrorSendPlacingOrder={isErrorSendCreateReturnOrder}
            isErrorSendTopUpBalance={isErrorSendTopUpBalance}
            isLoadingSendPlacingOrder={isLoadingSendCreateReturnOrder}
            isLoadingSendTopUpBalance={isLoadingSendTopUpBalance}
            parcelsGood={parcelsGoodView}
            placingOrderInProcessed={orderInProcessed}
            placingOrderType={EPlacingOrder.UNCLAIMED_UNPAID}
        />
      </Modal>
      <Modal
          isOpen={isOpenPaymentWindow}
          onClickOverlay={onClosePaymentReturn}
          onClose={onClosePaymentReturn}
          width={560}
      >
        <PaymentModalWindow
            dataSendTopUpBalance={dataSendTopUpBalance!}
            errorGetPaymentLink={errorGetPaymentLink}
            handlerOnClickPaymentLink={onClickPaymentLink}
            isErrorGetPaymentLink={isErrorGetPaymentLink}
            isLoadingGetPaymentLink={isLoadingGetPaymentLink}
            isLoadingSendTopUpBalance={isLoadingSendTopUpBalance}
            mutateSendTopUpBalance={mutateSendTopUpBalance}
            paymentError={paymentError}
            paymentInProcessed={paymentInProcessed}
            selectedSum={selectedDelivery}
            subTitle={t('paymentModalWindow.subTitle_1')}
        />
      </Modal>
      <Modal
          isOpen={isOpenPaymentCompletedWindow}
          onClickOverlay={() => setIsOpenPaymentCompletedWindow(false)}
          onClose={() => setIsOpenPaymentCompletedWindow(false)}
          width={560}
      >
        <PaymentCompletedModalWindow
            action={() => navigate("/unclaimed?tab=returns")}
            btnText={t('returnModal.success.okBtn')}
            handlerSetIsOpenPaymentCompletedWindow={setIsOpenPaymentCompletedWindow}
            title={t('paymentCompletedModalWindow.info_1')}
        />
      </Modal>

      {/* Препродажи */}
      <Modal
        isOpen={isResalesOpen}
        onClose={() => {
          setIsResalesOpen(false);
        }}
        onClickOverlay={() => {
          setIsResalesOpen(false);
        }}
      >
        <ResaleModalWindow
          resale={resale}
          onSuccess={() => {
            handlerSetSavedSelection([], []);
            refreshData();
            setSelectedParcels(new Map());
            setIsResalesOpen(false);
            counter.refetch();
          }}
        />
      </Modal>

      {/* Подтверждение телефона */}
      <Modal
        isOpen={isHardConfirmOpen}
        onClose={() => {
          setIsHardConfirmOpen(false);
        }}
        onClickOverlay={() => {
          setIsHardConfirmOpen(false);
        }}
      >
        <HardConfirmPhoneModalWindow
          onSuccess={() => {
            get();
            setIsHardConfirmOpen(false);
          }}
          text={t("hardConfirmPhoneModalWindows.textResale")}
          isEditable
        />
      </Modal>

      <Modal
        isOpen={isFpbInstructionOpen}
        onClose={closeFpbInstruction}
        onClickOverlay={closeFpbInstruction}
        width={567}
      >
        <FpbInstruction onClose={closeFpbInstruction} />
      </Modal>

      {isOpenParcelWindow ? (
        <Modal
          isOpen={isOpenParcelWindow}
          onClose={closeParcelWindow}
          onClickOverlay={closeParcelWindow}
          width={900}
          bodyWithoutIndentations
        >
          <ParcelModalWindow
            parcelId={detailId}
          />
        </Modal>
      ) : null}
    </div>
  );
};

export default UnpaidTab;
